import { PACKAGE_STATUS_APPROVED } from '~/consts/package';

import PackagesService from '~/services/PackagesService';
import ReservationService from '~/services/ReservationService';

import { runInChunks } from '~/utils/deleteAllInChunks';
import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import type { TaxesAndFeesUpdatePayload } from '../TaxesFeesStep/formData';

import type { SubmissionSectionKey } from './buildDraftSummary';

export interface RollbackErrorEntry {
  message: string;
  section: SubmissionSectionKey;
}

export interface CreatedFlashResources {
  ratePlanIds: Array<string>;
  roomRates: Array<{ roomRateId: string; roomTypeId: string }>;
  roomTypeIds: Array<string>;
}

export interface PreExistingTaxesFeesSnapshot {
  previousPayload: TaxesAndFeesUpdatePayload;
  propertyId: string;
}

/**
 * A package's state before linking wrote to it. Linking is the wizard's only package write and
 * it writes each package independently, so a half-failed link leaves some packages pointing at
 * reservation resources this rollback is about to delete -- restoring the whole pre-write shape
 * (links and flexible-nights together, in one call) is what keeps them from dangling.
 */
export interface PreExistingPackageSnapshot {
  offerId: string;
  packageSalesforceId: string;
  previousFkPropertyId: string | null;
  previousFkRoomRateId: string | null;
  previousFkRoomTypeId: string | null;
  previousFlexibleNights: boolean;
  previousStatus?: string;
  /** The package's own uuid, which the status route takes in place of `packageSalesforceId`. */
  packageUuid?: string;
}

export interface RollbackFlashDraftParams {
  createdResources: CreatedFlashResources;
  packageSnapshots: Array<PreExistingPackageSnapshot>;
  propertyId: string;
  taxesFeesSnapshot?: PreExistingTaxesFeesSnapshot;
}

/**
 * Reverts a partially-submitted draft: restores pre-existing taxes & fees and package state, then
 * deletes created room rates first (a rate plan's room-rate FK is NO ACTION), rate plans, room types.
 * Every action is isolated (one failure never blocks the rest) and reported, not thrown.
 */
export async function rollbackCreatedFlashResources({
  createdResources,
  packageSnapshots,
  propertyId,
  taxesFeesSnapshot,
}: RollbackFlashDraftParams): Promise<{ errors: Array<RollbackErrorEntry> }> {
  const errors: Array<RollbackErrorEntry> = [];

  if (taxesFeesSnapshot) {
    try {
      await ReservationService.updatePropertyTaxesAndFees(
        taxesFeesSnapshot.previousPayload,
        taxesFeesSnapshot.propertyId,
      );
    } catch (error) {
      reportError(error);
      errors.push({
        message: `Unable to restore the property's previous taxes & fees: ${getErrorMessage(error)}`,
        section: 'taxesFees',
      });
    }
  }

  await runInChunks(
    packageSnapshots,
    async (snapshot) => {
      // The status goes first: svc-offer rejects clearing the links of a package that is still approved.
      // A package that was already approved kept its status, so there's nothing to put back.
      if (snapshot.previousStatus && snapshot.previousStatus !== PACKAGE_STATUS_APPROVED) {
        if (!snapshot.packageUuid) {
          throw new Error('the package has no uuid to restore its status with');
        }

        await PackagesService.updatePackageStatus(snapshot.previousStatus, snapshot.offerId, snapshot.packageUuid);
      }

      await PackagesService.updatePackage(
        {
          fk_property_id: snapshot.previousFkPropertyId,
          fk_room_rate_id: snapshot.previousFkRoomRateId,
          fk_room_type_id: snapshot.previousFkRoomTypeId,
          flexible_nights: snapshot.previousFlexibleNights,
        },
        snapshot.offerId,
        snapshot.packageSalesforceId,
      );
    },
    {
      failureMode: 'isolate',
      onError: (error, snapshot) => {
        reportError(error);
        errors.push({
          message: `Unable to restore package ${snapshot?.packageSalesforceId} to its previous room/rate, flexible-nights and status settings: ${getErrorMessage(error)}`,
          section: 'packages',
        });
      },
    },
  );

  await runInChunks(
    createdResources.roomRates,
    (roomRate) => ReservationService.deleteRoomRate(roomRate.roomRateId, propertyId, roomRate.roomTypeId),
    {
      failureMode: 'isolate',
      onError: (error, roomRate) => {
        reportError(error);
        errors.push({
          message: `Unable to delete room rate ${roomRate?.roomRateId}: ${getErrorMessage(error)}`,
          section: 'roomRates',
        });
      },
    },
  );

  await runInChunks(createdResources.ratePlanIds, (ratePlanId) => ReservationService.deleteRatePlan(ratePlanId), {
    failureMode: 'isolate',
    onError: (error, ratePlanId) => {
      reportError(error);
      errors.push({
        message: `Unable to delete rate plan ${ratePlanId}: ${getErrorMessage(error)}`,
        section: 'ratePlans',
      });
    },
  });

  await runInChunks(
    createdResources.roomTypeIds,
    (roomTypeId) => ReservationService.deleteRoomType(propertyId, roomTypeId),
    {
      failureMode: 'isolate',
      onError: (error, roomTypeId) => {
        reportError(error);
        errors.push({
          message: `Unable to delete room type ${roomTypeId}: ${getErrorMessage(error)}`,
          section: 'rooms',
        });
      },
    },
  );

  return { errors };
}
